import React, { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { productState, setProductData, setProductOptionsData } from '../store/slices/product';

import { useTranslation, Trans } from 'react-i18next';

import BasePage from './BasePage';
import { TextInput, RadioInput, CostCalculator } from '../components';

import { getCouponDiscount } from '../services/product';

/**
* Page that holds product form
*/
const ProductPage = () => {
  const { t } = useTranslation('product');
  const { productOptions, product } = useSelector(productState);
  const dispatch = useDispatch();

  const [currentLoading, setLoaderOn] = useState('');

  const productSelectCallback = (name, value) => dispatch(setProductData({ ...product, [name]: value }));
  useEffect(() => {
    const timer = setTimeout(() => {
      if (product.coupon) {
        setLoaderOn('coupon');
        getCouponDiscount(product.coupon)
          .then((discount) => dispatch(setProductData({ ...product, discount })))
          .catch(() => dispatch(setProductData({ ...product, discount: {} })))
          .finally(() => setLoaderOn(''));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [product.coupon]);

  useEffect(() => {
    if (productOptions.productList) {
      let current = productOptions.productList[0];
      if (product.product) {
        current = productOptions.productList.find((item) => item.id === product.product.id);
      }
      dispatch(setProductOptionsData({ ...productOptions, planList: current.planList }));
    }
  }, [product.product, productOptions.productList]);

  useEffect(() => {
    const param = {};
    const coupon = new URLSearchParams(window.location.search).get('cupom');
    if (coupon?.length > 0) {
      param['coupon'] = coupon;
    }

    if (!product.plan?.description) {
      const plan = productOptions.planList.find((item) => item.id === product.plan?.id);
      if (plan) {
        param['plan'] = plan;
      }
    }

    dispatch(setProductData({ ...product, ...param }));
  }, []);
  return (
    <BasePage
      title={t('title')}
      navigation={{
        secondary: { path: '/', text: t('secondaryButton') },
        primary: { path: '/payment', text: t('primaryButton') }
      }}>
      <div className='page__container'>
        {productOptions.productList && <RadioInput
          name='product'
          label={t('form.product.label')}
          defaultValue={product.product}
          options={{ data: productOptions.productList }}
          validation={{ required: t('form.product.required') }}
          onSelect={productSelectCallback}
        />}
        {productOptions.planList && <RadioInput
          name='plan'
          label={t('form.plan.label')}
          width={productOptions.productList ? 50 : 100}
          defaultValue={product.plan}
          options={{ data: productOptions.planList }}
          validation={{ required: t('form.plan.required') }}
          onSelect={productSelectCallback}
        />}
        {
          product.plan?.description && <div className='page__text page__text--special'>
            <strong>{t('description.title')}</strong>
            <br />
            <Trans t={t} i18nKey='description.text' values={{ text: product.plan?.description.replace(/[\n]/g, "<br/>") }} />
          </div>
        }
        {productOptions.periodList && <RadioInput
          name='period'
          label={t('form.period.label')}
          width={50}
          defaultValue={product.period}
          options={{ data: productOptions.periodList }}
          validation={{ required: t('form.period.required') }}
          onSelect={productSelectCallback}
        />}
        {productOptions.points && <TextInput
          name='points'
          label={t('form.points.label')}
          keyboard='number'
          width={productOptions.points ? 50 : 100}
          defaultValue={product.points}
          mask={[{ custom: 'number', length: 0 }]}
          validation={{
            required: t('form.points.required'),
            validate: (value) => parseInt(value) > 0 || t('form.points.invalid')
          }}
          onChange={productSelectCallback}
        />}
        <TextInput
          name='coupon'
          label={t('form.coupon.label')}
          defaultValue={product.coupon}
          loading={currentLoading === 'coupon'}
          onChange={productSelectCallback}
        />
      </div>
      <CostCalculator {...product} />
    </BasePage>
  );
}
export default ProductPage;